import React, { useState, useEffect, useCallback } from 'react';
import { fetchLooks, fetchDatabasePoses } from '../shared/api';

const Field = ({ label, children, half, error }) => (
  <div style={{ marginBottom: 12, display: 'inline-block', width: half ? '48%' : '100%', marginRight: half ? '4%' : 0, verticalAlign: 'top' }}>
    <label style={{ display: 'block', fontSize: 11, color: error ? '#d32f2f' : '#666', marginBottom: 3, fontWeight: 500 }}>{label}{error ? ' *' : ''}</label>
    {children}
    {error && <div style={{ fontSize: 10, color: '#d32f2f', marginTop: 2 }}>{error}</div>}
  </div>
);

const Input = ({ value, onChange, type = 'text', placeholder, error, onBlur, ...props }) => (
  <input
    type={type}
    value={value ?? ''}
    onChange={e => onChange(type === 'number' ? (e.target.value === '' ? null : Number(e.target.value)) : e.target.value)}
    onKeyDown={type === 'number' ? e => {
      if (e.shiftKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
        e.preventDefault();
        const step = parseFloat(e.target.step) || 1;
        const current = parseFloat(e.target.value) || 0;
        const delta = (e.key === 'ArrowUp' ? 1 : -1) * step * 10;
        onChange(Math.round((current + delta) * 1e6) / 1e6);
      }
    } : undefined}
    onBlur={onBlur}
    placeholder={placeholder}
    style={{
      width: '100%', padding: '6px 8px',
      border: `1px solid ${error ? '#d32f2f' : '#ddd'}`,
      borderRadius: 3, fontSize: 13, boxSizing: 'border-box'
    }}
    {...props}
  />
);

const Select = ({ value, onChange, options }) => (
  <select
    value={value || ''}
    onChange={e => onChange(e.target.value)}
    style={{
      width: '100%', padding: '6px 8px', border: '1px solid #ddd', borderRadius: 3,
      fontSize: 13, background: '#fff', boxSizing: 'border-box'
    }}
  >
    {options.map(o => (
      <option key={o.value} value={o.value}>{o.label}</option>
    ))}
  </select>
);

const SEASONS = [
  '', 'spring2026', 'fall2026', 'spring2027', 'fall2027'
].map(s => ({ value: s, label: s || '(none)' }));

const POSE_NUMBERS = Array.from({ length: 100 }, (_, i) => {
  const n = String(i + 1).padStart(2, '0');
  return { value: `-${n}`, label: `-${n}` };
});

const SourceField = ({ label, value, onChange, allLooks, error }) => {
  // Determine mode: if value is "1", "2", or "3" it's shot mode, otherwise look mode
  const isShot = value === '1' || value === '2' || value === '3' || value === 1 || value === 2 || value === 3;
  const [mode, setMode] = React.useState(isShot || value == null ? 'shot' : 'look');

  // Sync mode when value changes externally (e.g., switching to a different look)
  React.useEffect(() => {
    const nowShot = value === '1' || value === '2' || value === '3' || value === 1 || value === 2 || value === 3;
    setMode(nowShot || value == null ? 'shot' : 'look');
  }, [value]);

  const handleModeSwitch = (newMode) => {
    setMode(newMode);
    onChange(null); // clear on mode switch
  };

  return (
    <Field label={label} error={error}>
      <div style={{ display: 'flex', gap: 4, marginBottom: 6 }}>
        <button
          type="button"
          onClick={() => handleModeSwitch('shot')}
          style={{
            padding: '3px 10px', fontSize: 11, border: '1px solid #ccc', borderRadius: 3,
            background: mode === 'shot' ? '#1976d2' : '#fff',
            color: mode === 'shot' ? '#fff' : '#666', cursor: 'pointer'
          }}
        >
          Shot #
        </button>
        <button
          type="button"
          onClick={() => handleModeSwitch('look')}
          style={{
            padding: '3px 10px', fontSize: 11, border: '1px solid #ccc', borderRadius: 3,
            background: mode === 'look' ? '#1976d2' : '#fff',
            color: mode === 'look' ? '#fff' : '#666', cursor: 'pointer'
          }}
        >
          Look Name
        </button>
      </div>
      {mode === 'shot' ? (
        <select
          value={value ?? ''}
          onChange={e => onChange(e.target.value || null)}
          style={{
            width: '100%', padding: '6px 8px', border: `1px solid ${error ? '#d32f2f' : '#ddd'}`,
            borderRadius: 3, fontSize: 13, background: '#fff', boxSizing: 'border-box'
          }}
        >
          <option value="">(none)</option>
          <option value="1">Shot 1</option>
          <option value="2">Shot 2</option>
          <option value="3">Shot 3</option>
        </select>
      ) : (
        <input
          type="text"
          value={value ?? ''}
          onChange={e => onChange(e.target.value || null)}
          placeholder="e.g. SPR26-04"
          list="look-names-list"
          style={{
            width: '100%', padding: '6px 8px', border: `1px solid ${error ? '#d32f2f' : '#ddd'}`,
            borderRadius: 3, fontSize: 13, boxSizing: 'border-box'
          }}
        />
      )}
      {mode === 'look' && allLooks && (
        <datalist id="look-names-list">
          {allLooks.map(l => <option key={l.id} value={l.name} />)}
        </datalist>
      )}
    </Field>
  );
};

const CANVAS_PRESETS = [
  { label: 'Portrait 2400x3000', width: 2400, height: 3000 },
  { label: 'Landscape 3000x2400', width: 3000, height: 2400 },
  { label: 'Square 2400x2400', width: 2400, height: 2400 },
  { label: 'Custom', width: null, height: null },
];

// --- Collage Photos Section ---

const CollagePhotoSlot = ({ photo, index, onChange, onRemove, allLooks, isSelected, onSelect, canvasWidth, canvasHeight }) => {
  const [seasonLooks, setSeasonLooks] = useState([]);
  const [databasePoses, setDatabasePoses] = useState([]);
  const [loadingLooks, setLoadingLooks] = useState(false);
  const [loadingPoses, setLoadingPoses] = useState(false);
  // Source mode: 'look' for designer look templates, 'database' for existing processed poses
  const sourceMode = photo.source_type === 'database' ? 'database' : 'look';

  // Fetch looks when season changes
  useEffect(() => {
    if (!photo.season || sourceMode !== 'look') { setSeasonLooks([]); return; }
    if (allLooks) {
      setSeasonLooks(allLooks.filter(l => l.season === photo.season && l.look_type === 'single'));
    } else {
      setLoadingLooks(true);
      fetchLooks(photo.season).then(looks => {
        setSeasonLooks(looks.filter(l => l.look_type === 'single'));
      }).catch(() => setSeasonLooks([]))
        .finally(() => setLoadingLooks(false));
    }
  }, [photo.season, allLooks, sourceMode]);

  // Fetch database poses when season changes
  useEffect(() => {
    if (!photo.season || sourceMode !== 'database') { setDatabasePoses([]); return; }
    setLoadingPoses(true);
    fetchDatabasePoses(photo.season)
      .then(setDatabasePoses)
      .catch(() => setDatabasePoses([]))
      .finally(() => setLoadingPoses(false));
  }, [photo.season, sourceMode]);

  const handleSourceToggle = (mode) => {
    onChange({ ...photo, source_type: mode, look_ref: null, pose_id: null, pose_s3_key: null, pose_thumbnail_url: null });
  };

  const handleSeasonChange = (season) => {
    onChange({ ...photo, season, look_ref: null, pose_id: null, pose_s3_key: null, pose_thumbnail_url: null });
  };

  const setDefaultPosition = (updates, aspectRatio) => {
    if (updates.x != null) return; // already positioned
    const cw = canvasWidth || 2400;
    const ch = canvasHeight || 3000;
    const defaultH = 0.4;
    const defaultW = defaultH * (aspectRatio || 0.8) * (ch / cw);
    updates.height = Math.min(defaultH, 0.9);
    updates.width = Math.min(defaultW, 0.9);
    updates.x = Math.max(0, 0.5 - updates.width / 2);
    updates.y = Math.max(0, 0.5 - updates.height / 2);
  };

  const handleLookChange = (look_ref) => {
    const selectedLook = seasonLooks.find(l => l.name === look_ref);
    const updates = { ...photo, look_ref, source_type: 'look', pose_id: null, pose_s3_key: null, pose_thumbnail_url: null };
    if (look_ref && photo.x == null) {
      const lookAspect = selectedLook ? (selectedLook.canvas_width || 2400) / (selectedLook.canvas_height || 3000) : 0.8;
      setDefaultPosition(updates, lookAspect);
    }
    onChange(updates);
  };

  const handlePoseSelect = (pose) => {
    const updates = {
      ...photo,
      source_type: 'database',
      look_ref: null,
      pose_id: pose.id,
      pose_s3_key: pose.s3_key,
      pose_name: pose.name,
      pose_thumbnail_url: pose.thumbnail_url
    };
    if (photo.x == null) {
      setDefaultPosition(updates, 0.8); // assume portrait for poses
    }
    onChange(updates);
  };

  const hasPlacement = photo.x != null;
  const displayName = sourceMode === 'database' ? (photo.pose_name || `Pose #${photo.pose_id}`) : photo.look_ref;

  return (
    <div
      onClick={() => onSelect(index)}
      style={{
        padding: '8px 10px', marginBottom: 8,
        border: `2px solid ${isSelected ? '#1976d2' : '#e0e0e0'}`,
        borderRadius: 6, background: isSelected ? '#e3f2fd' : '#fff',
        cursor: 'pointer', position: 'relative'
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: '#333' }}>Photo {index + 1}</span>
        <button
          type="button"
          onClick={e => { e.stopPropagation(); onRemove(index); }}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            fontSize: 14, color: '#999', lineHeight: 1, padding: '2px 4px'
          }}
          title="Remove photo"
        >&times;</button>
      </div>

      {/* Source toggle */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 6 }}>
        <button type="button" onClick={e => { e.stopPropagation(); handleSourceToggle('look'); }}
          style={{
            padding: '3px 10px', fontSize: 10, border: '1px solid #ccc', borderRadius: 3,
            background: sourceMode === 'look' ? '#1976d2' : '#fff',
            color: sourceMode === 'look' ? '#fff' : '#666', cursor: 'pointer'
          }}>
          Look Template
        </button>
        <button type="button" onClick={e => { e.stopPropagation(); handleSourceToggle('database'); }}
          style={{
            padding: '3px 10px', fontSize: 10, border: '1px solid #ccc', borderRadius: 3,
            background: sourceMode === 'database' ? '#e65100' : '#fff',
            color: sourceMode === 'database' ? '#fff' : '#666', cursor: 'pointer'
          }}>
          Database Pose
        </button>
      </div>

      <Field label="Season" half>
        <Select
          value={photo.season || ''}
          onChange={handleSeasonChange}
          options={SEASONS}
        />
      </Field>

      {sourceMode === 'look' && (
        <Field label="Look" half>
          <select
            value={photo.look_ref || ''}
            onChange={e => handleLookChange(e.target.value || null)}
            disabled={!photo.season || loadingLooks}
            style={{
              width: '100%', padding: '6px 8px', border: '1px solid #ddd', borderRadius: 3,
              fontSize: 13, background: '#fff', boxSizing: 'border-box',
              opacity: (!photo.season || loadingLooks) ? 0.5 : 1
            }}
          >
            <option value="">{loadingLooks ? 'Loading...' : photo.season ? 'Select a look' : 'Select season first'}</option>
            {seasonLooks.map(l => (
              <option key={l.id || l.name} value={l.name}>{l.name}</option>
            ))}
          </select>
        </Field>
      )}

      {sourceMode === 'database' && (
        <Field label="Pose" half>
          <select
            value={photo.pose_id || ''}
            onChange={e => {
              const pose = databasePoses.find(p => String(p.id) === e.target.value);
              if (pose) handlePoseSelect(pose);
            }}
            disabled={!photo.season || loadingPoses}
            style={{
              width: '100%', padding: '6px 8px', border: '1px solid #ddd', borderRadius: 3,
              fontSize: 13, background: '#fff', boxSizing: 'border-box',
              opacity: (!photo.season || loadingPoses) ? 0.5 : 1
            }}
          >
            <option value="">{loadingPoses ? 'Loading...' : photo.season ? 'Select a pose' : 'Select season first'}</option>
            {databasePoses.map(p => (
              <option key={p.id} value={p.id}>{p.name} (Shot {p.number})</option>
            ))}
          </select>
        </Field>
      )}

      {/* Pose thumbnail preview */}
      {sourceMode === 'database' && photo.pose_thumbnail_url && (
        <div style={{ marginTop: 4 }}>
          <img src={photo.pose_thumbnail_url} style={{ maxWidth: 60, maxHeight: 75, borderRadius: 3, border: '1px solid #ddd' }} />
        </div>
      )}

      {hasPlacement && displayName && (
        <div style={{ fontSize: 10, color: '#888', marginTop: 4 }}>
          {sourceMode === 'database' && <span style={{ color: '#e65100', fontWeight: 500 }}>DB </span>}
          Position: ({(photo.x * 100).toFixed(1)}%, {(photo.y * 100).toFixed(1)}%)
          &nbsp;Size: {(photo.width * 100).toFixed(1)}% x {(photo.height * 100).toFixed(1)}%
        </div>
      )}
    </div>
  );
};

const CollagePhotosSection = ({ photos, onChange, allLooks, selectedPhotoIndex, onSelectPhoto, canvasWidth, canvasHeight }) => {
  const [dragFrom, setDragFrom] = useState(null);

  const updatePhoto = (index, updated) => {
    const next = [...photos];
    next[index] = updated;
    onChange(next);
  };

  const removePhoto = (index) => {
    const next = photos.filter((_, i) => i !== index);
    onChange(next);
    // Adjust selection
    if (selectedPhotoIndex === index) onSelectPhoto(null);
    else if (selectedPhotoIndex > index) onSelectPhoto(selectedPhotoIndex - 1);
  };

  const addPhoto = () => {
    onChange([...photos, { look_ref: null, season: null, x: null, y: null, width: null, height: null }]);
  };

  // Layer reorder via drag
  const handleDragStart = (e, index) => {
    e.dataTransfer.effectAllowed = 'move';
    setDragFrom(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, toIndex) => {
    e.preventDefault();
    if (dragFrom === null || dragFrom === toIndex) return;
    const next = [...photos];
    const [moved] = next.splice(dragFrom, 1);
    next.splice(toIndex, 0, moved);
    onChange(next);
    // Update selection to follow the moved item
    if (selectedPhotoIndex === dragFrom) onSelectPhoto(toIndex);
    else if (selectedPhotoIndex > dragFrom && selectedPhotoIndex <= toIndex) onSelectPhoto(selectedPhotoIndex - 1);
    else if (selectedPhotoIndex < dragFrom && selectedPhotoIndex >= toIndex) onSelectPhoto(selectedPhotoIndex + 1);
    setDragFrom(null);
  };

  return (
    <div>
      <h4 style={{ margin: '16px 0 8px', fontSize: 14, color: '#333' }}>Photos on Canvas</h4>
      <p style={{ fontSize: 10, color: '#999', margin: '0 0 8px' }}>
        Select looks to composite onto the canvas. Drag to reorder layers (bottom = back, top = front).
      </p>

      {photos.map((photo, i) => (
        <div
          key={i}
          draggable
          onDragStart={e => handleDragStart(e, i)}
          onDragOver={e => handleDragOver(e, i)}
          onDrop={e => handleDrop(e, i)}
          style={{ display: 'flex', alignItems: 'stretch', gap: 4, marginBottom: 2 }}
        >
          <div style={{
            width: 16, display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'grab', color: '#bbb', fontSize: 14, flexShrink: 0, userSelect: 'none'
          }} title="Drag to reorder layer">
            ⋮⋮
          </div>
          <div style={{ flex: 1 }}>
            <CollagePhotoSlot
              photo={photo}
              index={i}
              onChange={updated => updatePhoto(i, updated)}
              onRemove={removePhoto}
              allLooks={allLooks}
              isSelected={selectedPhotoIndex === i}
              onSelect={onSelectPhoto}
              canvasWidth={canvasWidth}
              canvasHeight={canvasHeight}
            />
          </div>
        </div>
      ))}

      <button
        type="button"
        onClick={addPhoto}
        style={{
          width: '100%', padding: '8px', border: '1px dashed #bbb', borderRadius: 4,
          background: '#fafafa', cursor: 'pointer', fontSize: 12, color: '#666',
          marginTop: 4
        }}
      >
        + Add Look to Canvas
      </button>
    </div>
  );
};

const LookTab = ({ formData, onChange, validationErrors, allLooks, referencePoses }) => {
  const [nameWarning, setNameWarning] = useState(null);
  const [selectedPreset, setSelectedPreset] = useState(() => {
    const match = CANVAS_PRESETS.findIndex(p =>
      p.width === formData.canvas_width && p.height === formData.canvas_height
    );
    return match >= 0 ? match : CANVAS_PRESETS.length - 1; // default to Custom
  });

  // Sync preset when formData changes externally (e.g., loading a look)
  useEffect(() => {
    const match = CANVAS_PRESETS.findIndex(p =>
      p.width === formData.canvas_width && p.height === formData.canvas_height
    );
    setSelectedPreset(match >= 0 ? match : CANVAS_PRESETS.length - 1);
  }, [formData.canvas_width, formData.canvas_height]);

  const handlePresetChange = (idx) => {
    setSelectedPreset(idx);
    const preset = CANVAS_PRESETS[idx];
    if (preset.width != null && preset.height != null) {
      onChange({ canvas_width: preset.width, canvas_height: preset.height });
    }
  };

  const handleWidthChange = (v) => {
    onChange({ canvas_width: v });
    // Switch to Custom if it no longer matches a preset
    const match = CANVAS_PRESETS.findIndex(p => p.width === v && p.height === formData.canvas_height);
    setSelectedPreset(match >= 0 ? match : CANVAS_PRESETS.length - 1);
  };

  const handleHeightChange = (v) => {
    onChange({ canvas_height: v });
    const match = CANVAS_PRESETS.findIndex(p => p.width === formData.canvas_width && p.height === v);
    setSelectedPreset(match >= 0 ? match : CANVAS_PRESETS.length - 1);
  };

  const handleNameBlur = () => {
    if (!formData.name || !allLooks) {
      setNameWarning(null);
      return;
    }
    const duplicate = allLooks.find(l =>
      l.name === formData.name && l.id !== formData.id
    );
    setNameWarning(duplicate ? 'This name is already in use. Please choose a unique name.' : null);
  };

  // Clear name warning when name changes
  useEffect(() => {
    if (nameWarning) setNameWarning(null);
  }, [formData.name]);

  const errors = validationErrors || {};

  const handleRefPhoto = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => onChange({ ref_photo_data: ev.target.result });
    reader.readAsDataURL(file);
  };

  return (
    <div>
      <h4 style={{ margin: '0 0 12px', fontSize: 14, color: '#333' }}>Template Info</h4>

      <Field label="Name" error={errors.name || nameWarning}>
        <Input
          value={formData.name}
          onChange={v => onChange({ name: v })}
          placeholder="e.g. SPR26-01"
          error={!!errors.name || !!nameWarning}
          onBlur={handleNameBlur}
        />
      </Field>

      <Field label="Season" half error={errors.season}>
        <Select value={formData.season} onChange={v => onChange({ season: v })} options={SEASONS} />
      </Field>

      <Field label="Pose" half>
        <Select value={formData.pose_number} onChange={v => onChange({ pose_number: v })} options={POSE_NUMBERS} />
      </Field>

      <Field label="Type">
        <Select
          value={formData.look_type}
          onChange={v => onChange({ look_type: v })}
          options={[{ value: 'single', label: 'Single' }, { value: 'collage', label: 'Collage' }]}
        />
      </Field>

      <h4 style={{ margin: '16px 0 12px', fontSize: 14, color: '#333' }}>Canvas</h4>

      <Field label="Preset">
        <select
          value={selectedPreset}
          onChange={e => handlePresetChange(Number(e.target.value))}
          style={{
            width: '100%', padding: '6px 8px', border: '1px solid #ddd', borderRadius: 3,
            fontSize: 13, background: '#fff', boxSizing: 'border-box'
          }}
        >
          {CANVAS_PRESETS.map((p, i) => (
            <option key={i} value={i}>{p.label}</option>
          ))}
        </select>
      </Field>

      {selectedPreset === CANVAS_PRESETS.length - 1 && (
        <>
          <Field label="Width (px)" half error={errors.canvas_width}>
            <Input type="number" value={formData.canvas_width} onChange={handleWidthChange} error={!!errors.canvas_width} />
          </Field>
          <Field label="Height (px)" half error={errors.canvas_height}>
            <Input type="number" value={formData.canvas_height} onChange={handleHeightChange} error={!!errors.canvas_height} />
          </Field>
          <Field label="DPI" half error={errors.canvas_dpi}>
            <Input type="number" value={formData.canvas_dpi} onChange={v => onChange({ canvas_dpi: v })} error={!!errors.canvas_dpi} />
          </Field>
        </>
      )}

      {formData.look_type !== 'collage' && (
        <>
          <h4 style={{ margin: '16px 0 12px', fontSize: 14, color: '#333' }}>Source Pose Selection</h4>
          <p style={{ fontSize: 10, color: '#999', margin: '-8px 0 8px' }}>
            Which photo to use when a student has 3, 2, or 1 pose. Choose a shot number (1–3) or an existing look name.
          </p>

          <SourceField
            label="Source if 3 poses"
            value={formData.source_if_3}
            onChange={v => onChange({ source_if_3: v })}
            allLooks={allLooks}
            error={errors.source_if_3}
          />
          <SourceField
            label="Source if 2 poses"
            value={formData.source_if_2}
            onChange={v => onChange({ source_if_2: v })}
            allLooks={allLooks}
            error={errors.source_if_2}
          />
          <SourceField
            label="Source if 1 pose"
            value={formData.source_if_1}
            onChange={v => onChange({ source_if_1: v })}
            allLooks={allLooks}
            error={errors.source_if_1}
          />
        </>
      )}

      {formData.look_type === 'collage' && (
        <>
          <h4 style={{ margin: '16px 0 12px', fontSize: 14, color: '#333' }}>Canvas Background</h4>

          <p style={{ fontSize: 10, color: '#999', margin: '0 0 8px' }}>
            Upload a reference image to preview the background while designing. Set the S3 path for production generation.
          </p>

          <Field label="Background Reference Image">
            <input
              type="file"
              accept="image/*"
              onChange={e => {
                const file = e.target.files[0];
                if (!file) return;
                const reader = new FileReader();
                reader.onload = (ev) => onChange({ canvas_bg_ref_data: ev.target.result });
                reader.readAsDataURL(file);
              }}
              style={{ fontSize: 12 }}
            />
          </Field>
          {formData.canvas_bg_ref_data && (
            <div style={{ marginBottom: 8 }}>
              <img src={formData.canvas_bg_ref_data} style={{ maxWidth: 160, borderRadius: 4, border: '1px solid #ddd' }} />
              <button
                onClick={() => onChange({ canvas_bg_ref_data: null })}
                style={{ display: 'block', marginTop: 4, fontSize: 11, color: '#d32f2f', background: 'none', border: 'none', cursor: 'pointer' }}
              >
                Remove
              </button>
            </div>
          )}

          <Field label="Canvas Filepath (S3 path)">
            <Input value={formData.canvas_filepath} onChange={v => onChange({ canvas_filepath: v })} placeholder="s3://bucket/path/" />
          </Field>
          <Field label="Canvas Filename">
            <Input value={formData.canvas_filename} onChange={v => onChange({ canvas_filename: v })} placeholder="background.jpg" />
          </Field>

          <CollagePhotosSection
            photos={formData.collage_photos || []}
            onChange={photos => onChange({ collage_photos: photos })}
            allLooks={allLooks}
            selectedPhotoIndex={formData._selectedCollagePhoto}
            onSelectPhoto={idx => onChange({ _selectedCollagePhoto: idx })}
            canvasWidth={formData.canvas_width}
            canvasHeight={formData.canvas_height}
          />
        </>
      )}

      <h4 style={{ margin: '16px 0 12px', fontSize: 14, color: '#333' }}>Reference Photo</h4>
      <p style={{ fontSize: 11, color: '#999', margin: '0 0 8px' }}>
        Choose from pricelist reference poses or upload a custom image. Designer preview only — not used in generation.
      </p>

      {/* Pose picker — show available reference poses from the pricelist */}
      {referencePoses && referencePoses.some(Boolean) && (
        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 11, color: '#666', marginBottom: 4, fontWeight: 500 }}>Choose from poses:</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {referencePoses.map((pose, i) => pose ? (
              <div
                key={i}
                onClick={() => onChange({ ref_photo_data: pose })}
                style={{
                  cursor: 'pointer', textAlign: 'center',
                  border: formData.ref_photo_data === pose ? '2px solid #1976d2' : '1px solid #ddd',
                  borderRadius: 4, padding: 2, background: formData.ref_photo_data === pose ? '#e3f2fd' : '#fff'
                }}
              >
                <img src={pose} style={{ width: 50, height: 62, objectFit: 'cover', borderRadius: 3, display: 'block' }} />
                <div style={{ fontSize: 9, color: '#666', marginTop: 2 }}>Shot {i + 1}</div>
              </div>
            ) : null)}
          </div>
        </div>
      )}

      <div style={{ fontSize: 11, color: '#999', marginBottom: 4 }}>Or upload custom:</div>
      <input type="file" accept="image/*" onChange={handleRefPhoto} style={{ fontSize: 12 }} />
      {formData.ref_photo_data && (
        <div style={{ marginTop: 8 }}>
          <img src={formData.ref_photo_data} style={{ maxWidth: 120, borderRadius: 4, border: '1px solid #ddd' }} />
          <button
            onClick={() => onChange({ ref_photo_data: null })}
            style={{ display: 'block', marginTop: 4, fontSize: 11, color: '#d32f2f', background: 'none', border: 'none', cursor: 'pointer' }}
          >
            Remove
          </button>
        </div>
      )}
    </div>
  );
};

export default LookTab;
